// /whats-new — release log. Mirrors the desktop app's WhatsNewSettings.tsx
// surface. Collapsible cards per release. Recognizes the release_manager.ps1
// section markers (✨ Features / 🐛 Bug Fixes / 🔧 Maintenance / 📦 Bundle
// Components) and swaps each for a colored Lucide icon + title.

import { useEffect, useState } from 'react';
import { ChevronDown, ExternalLink, Sparkles, Bug, Wrench, Package } from 'lucide-react';
import { SettingsSection } from '../components/settings/_primitives';

interface GithubRelease {
  tag_name: string;
  name: string;
  published_at: string;
  html_url: string;
  body: string;
  prerelease: boolean;
}

const CACHE_KEY = 'streamnook_whatsnew_cache_v1';
const CACHE_TTL_MS = 30 * 60 * 1000; // 30 min, matches in-app

interface Cache {
  fetched_at: number;
  releases: GithubRelease[];
}

function readCache(): GithubRelease[] | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const c = JSON.parse(raw) as Cache;
    if (Date.now() - c.fetched_at > CACHE_TTL_MS) return null;
    return c.releases;
  } catch {
    return null;
  }
}

function writeCache(releases: GithubRelease[]): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ fetched_at: Date.now(), releases }));
  } catch { /* quota */ }
}

export default function WhatsNew() {
  const [releases, setReleases] = useState<GithubRelease[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    const cached = readCache();
    if (cached) {
      setReleases(cached);
      setExpanded(new Set([cached[0]?.tag_name].filter(Boolean) as string[]));
      setLoading(false);
      return;
    }

    fetch('https://api.github.com/repos/StreamNook/StreamNook/releases?per_page=20')
      .then(r => {
        if (!r.ok) throw new Error(`github_${r.status}`);
        return r.json() as Promise<GithubRelease[]>;
      })
      .then(data => {
        setReleases(data);
        if (data[0]) setExpanded(new Set([data[0].tag_name]));
        writeCache(data);
      })
      .catch(err => setError(String(err)))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (tag: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">Changelog</h1>
        <p className="text-textSecondary mt-1">
          Every StreamNook release, the bundled component bumps, and the bug fixes shipped along with them.
        </p>
      </div>

      <SettingsSection label="Releases" bare>
        {loading && <p className="text-textMuted py-6 text-center">Loading releases…</p>}
        {error && <p className="text-error py-6 text-center">Couldn't reach GitHub. {error}</p>}

        {releases.map(release => {
          const open = expanded.has(release.tag_name);
          return (
            <article key={release.tag_name} className="settings-card overflow-hidden">
              <button
                onClick={() => toggle(release.tag_name)}
                className="group w-full flex items-center justify-between py-5 px-5 hover:bg-white/[0.02] transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-base font-semibold text-textPrimary group-hover:text-accent transition-colors">
                    {release.tag_name}
                  </span>
                  {release.prerelease && (
                    <span className="text-[11px] uppercase tracking-wide text-yellow-400">Pre-release</span>
                  )}
                  <span className="text-sm text-textMuted">
                    {new Date(release.published_at).toLocaleDateString(undefined, {
                      year: 'numeric', month: 'short', day: 'numeric',
                    })}
                  </span>
                </div>
                <ChevronDown className={`w-4 h-4 text-textSecondary transition-transform ${open ? 'rotate-180' : ''}`} />
              </button>

              {open && (
                <div className="px-5 pb-6 border-t border-[var(--color-borderSubtle)] pt-4">
                  <ReleaseBody body={release.body ?? ''} />
                  <div className="mt-5 pt-4 border-t border-[var(--color-borderSubtle)]">
                    <a
                      href={release.html_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm text-textMuted hover:text-accent transition-colors"
                    >
                      View on GitHub <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </div>
              )}
            </article>
          );
        })}
      </SettingsSection>
    </div>
  );
}

// ─── Markdown renderer ────────────────────────────────────────────────────
// Recognizes the release_manager.ps1 section markers + bullets, headings,
// blockquotes, horizontal rules, inline bold/italic/code.

interface RenderedBlock {
  type: 'h2' | 'h3' | 'h4' | 'section' | 'bullets' | 'blockquote' | 'hr' | 'paragraph';
  content?: string;
  bullets?: string[];
  section?: 'features' | 'fixes' | 'maintenance' | 'bundle';
  title?: string;
}

const SECTION_PATTERNS: Array<{ re: RegExp; key: RenderedBlock['section']; label: string }> = [
  { re: /^#{2,3}\s*✨\s*Features\b/i, key: 'features', label: 'Features' },
  { re: /^#{2,3}\s*🐛\s*Bug Fixes\b/i, key: 'fixes', label: 'Bug Fixes' },
  { re: /^#{2,3}\s*🔧\s*Maintenance\b/i, key: 'maintenance', label: 'Maintenance' },
  { re: /^#{2,3}\s*📦?\s*Bundle Components\b/i, key: 'bundle', label: 'Bundle Components' },
];

function parseBody(body: string): RenderedBlock[] {
  const lines = body.replace(/\r\n/g, '\n').split('\n');
  const blocks: RenderedBlock[] = [];
  let currentBullets: string[] | null = null;

  const flushBullets = () => {
    if (currentBullets && currentBullets.length) {
      blocks.push({ type: 'bullets', bullets: currentBullets });
    }
    currentBullets = null;
  };

  for (const raw of lines) {
    const line = raw.trimEnd();

    // Section markers (must match before generic headings)
    const matched = SECTION_PATTERNS.find(p => p.re.test(line));
    if (matched) {
      flushBullets();
      blocks.push({ type: 'section', section: matched.key, title: matched.label });
      continue;
    }

    if (/^#{4}\s+/.test(line)) {
      flushBullets();
      blocks.push({ type: 'h4', content: line.replace(/^#{4}\s+/, '') });
    } else if (/^#{3}\s+/.test(line)) {
      flushBullets();
      blocks.push({ type: 'h3', content: line.replace(/^#{3}\s+/, '') });
    } else if (/^#{2}\s+/.test(line)) {
      flushBullets();
      blocks.push({ type: 'h2', content: line.replace(/^#{2}\s+/, '') });
    } else if (/^\s*[-*]\s+/.test(line)) {
      if (!currentBullets) currentBullets = [];
      currentBullets.push(line.replace(/^\s*[-*]\s+/, ''));
    } else if (/^>\s?/.test(line)) {
      flushBullets();
      blocks.push({ type: 'blockquote', content: line.replace(/^>\s?/, '') });
    } else if (/^-{3,}\s*$/.test(line) || /^={3,}\s*$/.test(line)) {
      flushBullets();
      blocks.push({ type: 'hr' });
    } else if (line.trim()) {
      flushBullets();
      blocks.push({ type: 'paragraph', content: line });
    }
  }
  flushBullets();
  return blocks;
}

function renderInline(text: string): React.ReactNode[] {
  // Minimal inline markdown: **bold**, *italic*, `code`, [link](url)
  const nodes: React.ReactNode[] = [];
  const regex = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;
  let last = 0;
  let key = 0;
  let m: RegExpExecArray | null;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith('**')) {
      nodes.push(<strong key={key++} className="text-textPrimary font-semibold">{tok.slice(2, -2)}</strong>);
    } else if (tok.startsWith('`')) {
      nodes.push(
        <code key={key++} className="px-1.5 py-0.5 rounded bg-white/[0.06] border border-white/[0.06] font-mono text-[12px]">
          {tok.slice(1, -1)}
        </code>,
      );
    } else if (tok.startsWith('[')) {
      const m2 = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(tok);
      if (m2) {
        nodes.push(
          <a key={key++} href={m2[2]} target="_blank" rel="noreferrer" className="text-accent hover:underline">
            {m2[1]}
          </a>,
        );
      } else {
        nodes.push(tok);
      }
    } else {
      nodes.push(<em key={key++} className="italic">{tok.slice(1, -1)}</em>);
    }
    last = m.index + tok.length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

function ReleaseBody({ body }: { body: string }) {
  const blocks = parseBody(body);
  return (
    <div className="space-y-2 text-sm text-textSecondary leading-relaxed">
      {blocks.map((b, i) => {
        switch (b.type) {
          case 'h2':
            return <h3 key={i} className="text-lg font-semibold text-textPrimary mt-5 mb-2">{renderInline(b.content!)}</h3>;
          case 'h3':
            return <h4 key={i} className="text-base font-semibold text-textPrimary mt-4 mb-2">{renderInline(b.content!)}</h4>;
          case 'h4':
            return <h5 key={i} className="text-sm font-semibold text-textPrimary mt-3 mb-1">{renderInline(b.content!)}</h5>;
          case 'section': {
            const meta = sectionMeta(b.section!);
            return (
              <div key={i} className="flex items-center gap-2.5 mt-6 mb-3">
                <span className={meta.color}>{meta.icon}</span>
                <span className="text-base font-semibold text-textPrimary">{b.title}</span>
              </div>
            );
          }
          case 'bullets':
            return (
              <ul key={i} className="space-y-1.5 list-disc list-outside pl-5 marker:text-textMuted">
                {b.bullets!.map((bullet, j) => (
                  <li key={j}>{renderInline(bullet)}</li>
                ))}
              </ul>
            );
          case 'blockquote':
            return (
              <blockquote key={i} className="border-l-2 border-[var(--color-accent)]/40 pl-4 py-1 text-textSecondary">
                {renderInline(b.content!)}
              </blockquote>
            );
          case 'hr':
            return <div key={i} className="h-px bg-[var(--color-borderSubtle)] my-4" />;
          case 'paragraph':
            return <p key={i}>{renderInline(b.content!)}</p>;
          default:
            return null;
        }
      })}
    </div>
  );
}

function sectionMeta(section: NonNullable<RenderedBlock['section']>): { icon: React.ReactNode; color: string } {
  switch (section) {
    case 'features':    return { icon: <Sparkles className="w-4 h-4" />, color: 'text-yellow-400' };
    case 'fixes':       return { icon: <Bug className="w-4 h-4" />,      color: 'text-red-400' };
    case 'maintenance': return { icon: <Wrench className="w-4 h-4" />,   color: 'text-blue-400' };
    case 'bundle':      return { icon: <Package className="w-4 h-4" />,  color: 'text-purple-400' };
  }
}
